/**
 * React Query hooks for an agent's MCP connections, backed by the Squad MCP API
 * (`src/services/mcpServers.ts`). Same token mechanism as the agent hooks
 * ({@link useSquadAppToken}); queries stay disabled until the app-token resolves.
 *
 * `useAgentConnections` joins the catalog, the workspace servers, and the
 * agent's links into a per-integration view plus the `connectedApps` set the
 * scope picker gates on — see `connectionModel.ts`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  attachAgentMcpServer,
  connectMcpCatalog,
  detachAgentMcpServer,
  listAgentMcpServers,
  listMcpCatalog,
  listWorkspaceMcpServers,
  setAgentMcpServerEnabled,
  setAgentMcpServerScopes
} from 'src/services/mcpServers'

import { useSquadAppToken } from '../../squad'

import {
  buildConnections,
  connectedApps,
  type CatalogConnection
} from './connectionModel'
import { agentQueryKeys } from './useWorkspaceAgents'

export const mcpQueryKeys = {
  catalog: (workspaceId: number) =>
    ['squad', 'mcp', workspaceId, 'catalog'] as const,
  servers: (workspaceId: number) =>
    ['squad', 'mcp', workspaceId, 'servers'] as const,
  agentLinks: (workspaceId: number, agentId: string) =>
    ['squad', 'mcp', workspaceId, 'agent-links', agentId] as const
}

/**
 * Catalog + workspace servers + this agent's links, folded into the
 * per-integration view and the connected-apps set. Returns `undefined` data
 * while the app-token is still resolving.
 */
export const useAgentConnections = (
  workspaceId: number,
  agentId: string | undefined
): {
  connections: CatalogConnection[]
  connectedApps: Set<string>
  isLoading: boolean
  isError: boolean
  appToken: string | undefined
} => {
  const appToken = useSquadAppToken(workspaceId)
  const enabled = Boolean(appToken && agentId)

  const catalog = useQuery({
    queryKey: mcpQueryKeys.catalog(workspaceId),
    queryFn: () => listMcpCatalog(appToken!),
    enabled: Boolean(appToken)
  })
  const servers = useQuery({
    queryKey: mcpQueryKeys.servers(workspaceId),
    queryFn: () => listWorkspaceMcpServers(appToken!),
    enabled: Boolean(appToken)
  })
  const links = useQuery({
    queryKey: mcpQueryKeys.agentLinks(workspaceId, agentId ?? ''),
    queryFn: () => listAgentMcpServers(appToken!, agentId!),
    enabled
  })

  const catalogRows = catalog.data ?? []
  const serverRows = servers.data ?? []
  const linkRows = links.data ?? []

  return {
    connections: buildConnections(catalogRows, serverRows, linkRows),
    connectedApps: connectedApps(linkRows, serverRows),
    isLoading: catalog.isLoading || servers.isLoading || links.isLoading,
    isError: catalog.isError || servers.isError || links.isError,
    appToken
  }
}

const useInvalidateConnections = (workspaceId: number, agentId: string) => {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({
      queryKey: mcpQueryKeys.catalog(workspaceId)
    })
    void queryClient.invalidateQueries({
      queryKey: mcpQueryKeys.servers(workspaceId)
    })
    void queryClient.invalidateQueries({
      queryKey: mcpQueryKeys.agentLinks(workspaceId, agentId)
    })
    // mcpServerCount on the agent detail/list shifts when links change.
    void queryClient.invalidateQueries({
      queryKey: agentQueryKeys.detail(workspaceId, agentId)
    })
    void queryClient.invalidateQueries({
      queryKey: agentQueryKeys.list(workspaceId)
    })
  }
}

/**
 * Connect a catalog preset and attach it to the agent in one step: provisions
 * (or refreshes) the workspace server, then links it. Cross-app presets pass
 * the chosen instance token + label.
 */
export const useConnectMcp = (workspaceId: number, agentId: string) => {
  const appToken = useSquadAppToken(workspaceId)
  const invalidate = useInvalidateConnections(workspaceId, agentId)

  return useMutation({
    mutationFn: async (input: {
      slug: string
      app_token?: string
      name?: string
    }) => {
      const server = await connectMcpCatalog(appToken!, input.slug, {
        app_token: input.app_token,
        name: input.name
      })

      return attachAgentMcpServer(appToken!, agentId, {
        server: server.id,
        is_enabled: true
      })
    },
    onSuccess: invalidate
  })
}

export const useToggleAgentMcp = (workspaceId: number, agentId: string) => {
  const appToken = useSquadAppToken(workspaceId)
  const invalidate = useInvalidateConnections(workspaceId, agentId)

  return useMutation({
    mutationFn: (vars: { linkId: number; isEnabled: boolean }) =>
      setAgentMcpServerEnabled(appToken!, agentId, vars.linkId, vars.isEnabled),
    onSuccess: invalidate
  })
}

/** Replace the delegated scopes granted for one connection (per MCP instance). */
export const useSetAgentMcpScopes = (workspaceId: number, agentId: string) => {
  const appToken = useSquadAppToken(workspaceId)
  const invalidate = useInvalidateConnections(workspaceId, agentId)

  return useMutation({
    mutationFn: (vars: { linkId: number; scopes: string }) =>
      setAgentMcpServerScopes(appToken!, agentId, vars.linkId, vars.scopes),
    onSuccess: invalidate
  })
}

export const useDetachAgentMcp = (workspaceId: number, agentId: string) => {
  const appToken = useSquadAppToken(workspaceId)
  const invalidate = useInvalidateConnections(workspaceId, agentId)

  return useMutation({
    mutationFn: (linkId: number) =>
      detachAgentMcpServer(appToken!, agentId, linkId),
    onSuccess: invalidate
  })
}
